import { useIssues } from "@/pages/TriagePage/application/useTriage"
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react"

import { isVendorTicketingSystem } from "@/domain/ticketing"
import { getCreatedTicket } from "@/helpers/ticketing.helpers"
import { JiraIssueModal } from "../components/JiraIssueModal"
import { TicketIssueModal } from "../components/TicketIssueModal"
import { TicketSystemPickerModal } from "../components/TicketSystemPickerModal"
import { useHasTicketingIntegration } from "./useHasTicketingIntegration"

import type { VendorTicketingSystem } from "@/domain/ticketing"
import type { VendorTicketPayload } from "@/helpers/ticketing.helpers.types"
import type {
  CallbackFn,
  OpenTicketModalOptions,
  TicketingSystem,
  TicketModalPayload,
  TicketModalState,
  TTicketingContext
} from "./useTicketing.types"

const initialState: TTicketingContext = {
  systems: [],
  issuesDisabledOwners: [],
  openTicketModal: () => {}
}
const TicketingContext = createContext<TTicketingContext>(initialState)

export class UserClosedModalError extends Error {
  constructor() {
    super("User closed modal")
    this.name = "UserClosedModalError"
  }
}

export const isUserClosedModalError = (error: Error | unknown) => {
  return error instanceof UserClosedModalError
}

// One connected system is used as is; with several, the caller's choice or the selection
const resolveSystem = (
  systems: TicketingSystem[],
  system?: TicketingSystem
): TicketingSystem | null => {
  if (system) return system

  return systems.length === 1 ? systems[0] : null
}

export function TicketingProvider(props: PropsWithChildren) {
  const { createTicket, createTicketTriage } = useIssues()
  const { ticketingSystems: systems, issuesDisabledOwners } =
    useHasTicketingIntegration()
  const [modal, setModal] = useState<TicketModalState | null>(null)
  const onCreateCallbackRef = useRef<CallbackFn | null>(null)

  // to avoid multiple calls to the modal when the user is creating a ticket
  const openTicketModal = useCallback(
    ({
      payload,
      cb,
      system,
      systems: offered = systems
    }: OpenTicketModalOptions) => {
      if (modal || !offered.length) return
      onCreateCallbackRef.current = cb || null
      setModal({
        payload,
        systems: offered,
        system: resolveSystem(offered, system)
      })
    },
    [modal, systems]
  )

  const value = useMemo(
    () => ({ systems, issuesDisabledOwners, openTicketModal }),
    [systems, issuesDisabledOwners, openTicketModal]
  )

  const handleCreateTicket = async (
    data: Jira.CreateIssuePayload,
    ticket?: TicketModalPayload
  ) => {
    const cb = onCreateCallbackRef.current
    try {
      const response = await createTicket(data, ticket)

      if (response) {
        const data = response.data as unknown as {
          id: string
          name: string
          url: string
        }
        cb?.({
          data: {
            id: data.id,
            ticketName: data.name,
            url: data.url,
            system: "jira"
          }
        })
        setModal(null)
      }
      // jira types are not well defined
      // Need to fix with BE
      return response as unknown as KosmosApi.Response<any>
    } catch {
      cb?.({ err: new UserClosedModalError() })
    } finally {
      onCreateCallbackRef.current = null
    }
  }

  // Errors reach the modal, which shows them and stays open for another try;
  // the caller hears back once a ticket exists or the modal is closed
  const handleCreateVendorTicket = async (
    system: VendorTicketingSystem,
    data: VendorTicketPayload
  ) => {
    const ticket = getCreatedTicket(await createTicket(data, modal?.payload))

    if (!ticket) throw new Error("The created ticket was not returned")

    onCreateCallbackRef.current?.({
      data: { id: ticket.id, ticketName: ticket.name, url: ticket.url, system }
    })
    onCreateCallbackRef.current = null
    setModal(null)

    return ticket
  }

  const handleCloseModal = () => {
    setModal(null)
    const cb = onCreateCallbackRef.current
    try {
      cb?.({ err: new UserClosedModalError() })
    } finally {
      onCreateCallbackRef.current = null
    }
  }

  const system = modal?.system

  return (
    <TicketingContext.Provider value={value}>
      {props.children}

      {modal && system === null && (
        <TicketSystemPickerModal
          open
          systems={modal.systems}
          onSelect={(selected) => setModal({ ...modal, system: selected })}
          onCancel={handleCloseModal}
        />
      )}
      {modal && system === "jira" && (
        <JiraIssueModal
          type="jira"
          jira={modal.payload}
          closeModal={handleCloseModal}
          onCreateJiraIssue={handleCreateTicket}
          createJiraIssueMutation={createTicketTriage}
        />
      )}
      {modal && isVendorTicketingSystem(system) && (
        <TicketIssueModal
          key={system}
          system={system}
          payload={modal.payload}
          closeModal={handleCloseModal}
          onCreate={(data) => handleCreateVendorTicket(system, data)}
        />
      )}
    </TicketingContext.Provider>
  )
}

export const useTicketing = () => {
  const context = useContext(TicketingContext)

  if (!context) {
    throw new Error("useTicketing must be used within a TicketingProvider")
  }

  return context
}
